import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { SebastianQuestions } from '~/contracts/vdp';
import type { DeepPartial } from '~/types';

export type SebastianQuestion = {
  question: string;
  id: string;
  section: string;
};

export enum SebastianStatus {
  Loading = 'loading',
  Ready = 'ready',
  Error = 'error'
}

export type ContextType = {
  questions: LoadableState<SebastianQuestions>;
  sebastianStatus: SebastianStatus;
  setSebastianStatus: (status: SebastianStatus) => void;
};

const defaultContext: ContextType = {
  questions: defaultLoadableState<SebastianQuestions>({
    questions: [],
    question_version: ''
  }),
  sebastianStatus: SebastianStatus.Loading,
  setSebastianStatus: () => {}
};

export const SebastianContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{}>;

export const SebastianContextProvider = ({ children }: OwnProps) => {
  const [sebastianStatus, setSebastianStatus] = useState(
    SebastianStatus.Loading
  );
  const questions = useLoadableState(defaultContext.questions.data);

  const value = useMemo<ContextType>(
    () => ({
      questions,
      sebastianStatus,
      setSebastianStatus
    }),
    [questions, sebastianStatus]
  );

  return (
    <SebastianContext.Provider value={value}>
      {children}
    </SebastianContext.Provider>
  );
};

SebastianContextProvider.displayName = 'SebastianContextProvider';

export default SebastianContext;

export const generateSebastianContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useIsGenericQuestions() {
  return (
    useContext(SebastianContext).questions.data.question_version === 'generic'
  );
}

export function useSebastianStatus() {
  return useContext(SebastianContext).sebastianStatus;
}

export function useSetSebastianStatus() {
  return useContext(SebastianContext).setSebastianStatus;
}
